import {
  createSelector,
  createSlice,
  EntityId,
  PayloadAction,
} from '@reduxjs/toolkit';
import { RootState } from '../services/store';

import { PURGE } from 'redux-persist';

import { LibraryState } from '../types/library';
import { selectProjectById } from '../services/library';
import { SortDirection, SortType } from '../types/common';

const initialState: LibraryState = {
  page: 0,
  options: {
    sortType: SortType.REG_DATE,
    sortOrder: SortDirection.DESC,
    layoutType: 'grid',
    isEditMode: false,
    isDetailsOpened: false,
  },
  selectedIds: [],
  selectedItem: null,
};

export const librarySlice = createSlice({
  name: 'library',
  initialState,
  reducers: {
    libPageChanged: (state, action) => {
      state.page = action.payload;
    },
    libSortTypeChanged: (state, action) => {
      state.options.sortType = action.payload;
    },
    libOrderChanged: (state, action: PayloadAction<SortDirection, string>) => {
      state.options.sortOrder = action.payload;
    },
    libLayoutTypeChanged: (state, action) => {
      state.options.layoutType = action.payload;
    },
    libEditModeToggled: (state, action) => {
      state.options.isEditMode = action.payload;
    },
    libDetailsToggled: (
      state,
      action: PayloadAction<boolean | undefined, string>
    ) => {
      state.options.isDetailsOpened =
        action.payload ?? !state.options.isDetailsOpened;
    },
    libItemSelected: (state, action) => {
      state.selectedIds = action.payload.ids;
      if (action.payload.item !== undefined) {
        state.selectedItem = action.payload.item;
      }
    },
    libIndexListChanged: (state, action) => {
      if (state.selectedItem) {
        state.selectedItem.projectIndex.indexList = action.payload;
      }
    },
    libIndexIndentChanged: (state, action) => {
      const item = state.selectedItem?.projectIndex.indexList[action.payload];
      if (item) {
        item.indentYn = item.indentYn ? 0 : 1;
      }
    },
    libIndexListContentChanged: (state, { payload }) => {
      const item = state.selectedItem?.projectIndex.indexList[payload.idx];
      if (item) {
        item.content = payload.content;
      }
    },
    libIndexListPageChanged: (state, { payload }) => {
      const item = state.selectedItem?.projectIndex.indexList[payload.idx];
      if (item) {
        item.page = payload.page;
      }
    },
    libShareLinkChanged: (state, action) => {
      if (state.selectedItem) {
        state.selectedItem.projectShare.shareLink = action.payload;
      }
    },
    libShareUserChanged: (state, action) => {
      if (state.selectedItem) {
        state.selectedItem.projectShare.shareUser = action.payload;
      }
    },
    libHistoryChanged: (state, action) => {
      if (state.selectedItem)
        state.selectedItem.projectHistoryList = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(PURGE, () => initialState);
  },
});

export const {
  libPageChanged,
  libSortTypeChanged,
  libOrderChanged,
  libLayoutTypeChanged,
  libEditModeToggled,
  libDetailsToggled,
  libItemSelected,
  libIndexListChanged,
  libIndexIndentChanged,
  libIndexListContentChanged,
  libIndexListPageChanged,
  libShareLinkChanged,
  libShareUserChanged,
  libHistoryChanged,
} = librarySlice.actions;

export const selectLibPage = (state: RootState) => state.library.page;

export const selectLibOptions = (state: RootState) => state.library.options;

export const selectLibSelectedIds = (state: RootState): EntityId[] =>
  state.library.selectedIds;

export const selectLibSelectedId = createSelector(
  selectLibSelectedIds,
  (selectedIds) => selectedIds[0]
);

export const selectLibSelectedItems = (state: RootState) =>
  state.library.selectedIds.map((id: EntityId) => selectProjectById(state, id));

export const selectLibSingleItem = createSelector(
  selectLibSelectedItems,
  (selectedItems) => selectedItems[0]
);

export const selectLibSelectedItem = (state: RootState) =>
  state.library.selectedItem;

export const selectLibProject = createSelector(
  selectLibSelectedItem,
  (selectedItem) => selectedItem?.project
);

export const selectLibProjectResource = createSelector(
  selectLibSelectedItem,
  (selectedItem) => selectedItem?.projectResource
);

export const selectProjectShare = createSelector(
  selectLibSelectedItem,
  (selectedItem) => selectedItem?.projectShare
);

export const selectProjectPublic = createSelector(
  selectLibSelectedItem,
  (selectedItem) => selectedItem?.projectPublic
);

export const selectProjectHistoryList = createSelector(
  selectLibSelectedItem,
  (selectedItem) => selectedItem?.projectHistoryList
);

export default librarySlice.reducer;
